import { useState, useEffect, useCallback } from 'react';
import { Bell, BellOff, CheckCircle, Clock, AlertTriangle, ArrowRight, ArrowLeft, Loader2 } from 'lucide-react';
import { api } from '../../api/client';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './responsavel-mobile.css';
import { ROUTES } from '../../api/routes';
import { useResponsavelId } from './useResponsavelId';
import { registerAuthenticatedPushToken } from '../../security/pushApi';
import { NotificacaoDetalheSheet } from '../../components/notifications/NotificacaoDetalheSheet';
import { UserAvatar } from '../../components/ui/UserAvatar';
import { ResponsavelTabBar } from '../../components/responsavel/ResponsavelTabBar';

interface Notificacao {
  id: string;
  dataHora: string;
  tipoNotificacao: string;
  canal: string;
  status: string;
  titulo: string | null;
  mensagem: string;
  lidaEm: string | null;
  aluno?: { id: string; nome: string; matricula: string; fotoUrl?: string | null } | null;
}

const TIPO_ICONE: Record<string, { icon: typeof Bell; color: string; bg: string }> = {
  ENTRADA_ALUNO: { icon: ArrowRight, color: '#16a34a', bg: '#f0fdf4' },
  SAIDA_ALUNO: { icon: ArrowRight, color: '#3b82f6', bg: '#eff6ff' },
  PRESENCA_CONFIRMADA: { icon: CheckCircle, color: '#16a34a', bg: '#f0fdf4' },
  AUSENCIA_CHAMADA: { icon: AlertTriangle, color: '#dc2626', bg: '#fef2f2' },
  ATRASO: { icon: Clock, color: '#f59e0b', bg: '#fffbeb' },
  COMUNICADO: { icon: Bell, color: '#8b5cf6', bg: '#f5f3ff' },
  INCIDENTE: { icon: AlertTriangle, color: '#ef4444', bg: '#fef2f2' },
};

const FIREBASE_CONFIG = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY as string,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID as string,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET as string,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID as string,
  appId: import.meta.env.VITE_FIREBASE_APP_ID as string,
};
const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY as string;

async function obtainFcmToken(): Promise<string> {
  const { initializeApp, getApps } = await import('firebase/app');
  const { getMessaging, getToken } = await import('firebase/messaging');
  const app = getApps().length > 0 ? getApps()[0] : initializeApp(FIREBASE_CONFIG);
  const messaging = getMessaging(app);
  const registration = await navigator.serviceWorker.ready;
  const token = await getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: registration,
  });
  if (!token) throw new Error('Não foi possível obter o token de push.');
  return token;
}

/** Card de status/ativação manual de push — útil no cockpit e no app real. */
function PushStatusCard() {
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>('default');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(() => {
    if (typeof window === 'undefined' || !('Notification' in window) || typeof (window as any).Notification === 'undefined') {
      setPermission('unsupported');
      return;
    }
    setPermission((window as any).Notification.permission);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const ativar = async () => {
    if (typeof window === 'undefined' || !('Notification' in window) || typeof (window as any).Notification === 'undefined') {
      setError('Este navegador não suporta notificações.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const result = await (window as any).Notification.requestPermission();
      setPermission(result);
      if (result !== 'granted') {
        setError(
          result === 'denied'
            ? 'Permissão bloqueada. Libere nas configurações do navegador/site e tente de novo.'
            : 'Permissão não concedida.',
        );
        return;
      }
      const fcmToken = await obtainFcmToken();
      localStorage.setItem('ac_fcm_token', fcmToken);
      const jwt =
        localStorage.getItem('ac_token') || sessionStorage.getItem('ac_token') || '';
      await registerAuthenticatedPushToken(
        { fcmToken, plataforma: 'WEB', tipo: 'responsavel_notificacoes_manual' },
        { bearerToken: jwt },
      );
      try {
        sessionStorage.removeItem('ac_force_push');
        localStorage.removeItem('ac_force_push');
      } catch { /* noop */ }
    } catch (e: any) {
      setError(e?.message || 'Falha ao ativar notificações.');
    } finally {
      setBusy(false);
    }
  };

  if (permission === 'unsupported') {
    return (
      <div style={{
        background: '#f8fafc', borderRadius: 16, padding: 14, marginBottom: 12,
        border: '1px solid #e2e8f0', display: 'flex', gap: 12, alignItems: 'flex-start',
      }}>
        <BellOff size={20} style={{ color: '#94a3b8', flexShrink: 0, marginTop: 2 }} />
        <div>
          <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#475569' }}>Notificações indisponíveis</div>
          <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#94a3b8', lineHeight: 1.4 }}>
            Este ambiente não permite push. Use um celular ou o PWA instalado.
          </p>
        </div>
      </div>
    );
  }

  if (permission === 'granted') {
    return (
      <div style={{
        background: '#f0fdf4', borderRadius: 16, padding: '10px 14px', marginBottom: 12,
        border: '1px solid #bbf7d0', display: 'flex', gap: 10, alignItems: 'center',
      }}>
        <CheckCircle size={18} style={{ color: '#16a34a', flexShrink: 0 }} />
        <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#15803d' }}>
          Notificações ativas
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: permission === 'denied' ? '#fef2f2' : '#fffbeb',
      borderRadius: 16,
      padding: 14,
      marginBottom: 12,
      border: `1px solid ${permission === 'denied' ? '#fecaca' : '#fde68a'}`,
    }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        {permission === 'denied'
          ? <BellOff size={20} style={{ color: '#ef4444', flexShrink: 0, marginTop: 2 }} />
          : <Bell size={20} style={{ color: '#d97706', flexShrink: 0, marginTop: 2 }} />}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: '0.85rem',
            fontWeight: 700,
            color: permission === 'denied' ? '#b91c1c' : '#92400e',
          }}>
            {permission === 'denied' ? 'Notificações bloqueadas' : 'Ative as notificações'}
          </div>
          <p style={{
            margin: '4px 0 10px',
            fontSize: '0.75rem',
            color: permission === 'denied' ? '#991b1b' : '#a16207',
            lineHeight: 1.4,
          }}>
            {permission === 'denied'
              ? 'O navegador bloqueou o push. Nas configurações do site, permita notificações e toque em tentar novamente.'
              : 'Receba em tempo real quando o aluno entrar ou sair da escola e avisos da chamada.'}
          </p>
          {error && (
            <p style={{ margin: '0 0 10px', fontSize: '0.72rem', color: '#dc2626', lineHeight: 1.35 }}>
              {error}
            </p>
          )}
          <button
            type="button"
            onClick={() => { void ativar(); }}
            disabled={busy}
            style={{
              width: '100%',
              padding: '11px 14px',
              borderRadius: 12,
              border: 'none',
              cursor: busy ? 'wait' : 'pointer',
              background: permission === 'denied'
                ? 'linear-gradient(135deg, #ef4444, #dc2626)'
                : 'linear-gradient(135deg, #f59e0b, #d97706)',
              color: '#fff',
              fontSize: '0.82rem',
              fontWeight: 800,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              opacity: busy ? 0.75 : 1,
            }}
          >
            {busy ? (
              <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Ativando…</>
            ) : (
              <><Bell size={16} /> {permission === 'denied' ? 'Tentar novamente' : 'Ativar notificações'}</>
            )}
          </button>
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function useIsMobile(bp = 768) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth < bp : false);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < bp);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [bp]);
  return isMobile;
}

export function Notificacoes() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const responsavelId = useResponsavelId();
  const [notificacoes, setNotificacoes] = useState<Notificacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Notificacao | null>(null);

  const carregar = useCallback((silent = false) => {
    if (!responsavelId) return;
    if (!silent) setLoading(true);
    api.get(ROUTES.mobileResponsavel.timeline(responsavelId, 60))
      .then((res) => setNotificacoes(res.data || []))
      .catch(() => setNotificacoes([]))
      .finally(() => { if (!silent) setLoading(false); });
  }, [responsavelId]);

  useEffect(() => {
    carregar(false);
    const onPush = () => carregar(true);
    const onVis = () => { if (document.visibilityState === 'visible') carregar(true); };
    window.addEventListener('ac-push-received', onPush);
    window.addEventListener('focus', onPush);
    document.addEventListener('visibilitychange', onVis);
    const poll = window.setInterval(() => {
      if (document.visibilityState === 'visible') carregar(true);
    }, 15000);
    return () => {
      window.removeEventListener('ac-push-received', onPush);
      window.removeEventListener('focus', onPush);
      document.removeEventListener('visibilitychange', onVis);
      window.clearInterval(poll);
    };
  }, [carregar]);

  const marcarLida = useCallback(async (id: string) => {
    if (!responsavelId) return;
    try {
      await api.patch(ROUTES.mobileResponsavel.notificacaoLida(responsavelId, id));
      setNotificacoes((prev) =>
        prev.map((n) => (n.id === id ? { ...n, lidaEm: new Date().toISOString(), status: 'LIDA' } : n)),
      );
    } catch {
      // silencioso
    }
  }, [responsavelId]);

  const abrirDetalhe = useCallback((n: Notificacao) => {
    setSelected(n);
    if (!n.lidaEm) void marcarLida(n.id);
  }, [marcarLida]);

  // Deep-link: push/toast → ?notificacaoId= abre o detalhe
  useEffect(() => {
    const id = searchParams.get('notificacaoId');
    if (!id || loading) return;
    const found = notificacoes.find((n) => n.id === id);
    if (found) {
      abrirDetalhe(found);
      const next = new URLSearchParams(searchParams);
      next.delete('notificacaoId');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, notificacoes, loading, abrirDetalhe]);

  const naoLidas = notificacoes.filter((n) => !n.lidaEm).length;

  // ═══ DESKTOP VIEW ═══
  if (!isMobile) {
    return (
      <div className="admin-page" style={{ maxWidth: 1400, margin: '0 auto', paddingBottom: 32 }}>
        {/* ═══ Standardized Hero Banner ═══ */}
        <div style={{
          background: 'radial-gradient(135% 140% at 100% 0%, #312e81 0%, #1e1b4b 55%, #0f172a 100%)',
          borderRadius: 20,
          padding: '20px 28px',
          color: '#ffffff',
          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.14)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 16,
          marginBottom: 20,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{ minWidth: 260, position: 'relative', zIndex: 2 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)',
              padding: '3px 10px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 600,
              color: '#e0e7ff', marginBottom: 6,
            }}>
              <Bell size={12} color="#818cf8" /> Central de Rastreabilidade & Alertas
            </div>
            <h1 style={{ fontSize: '1.45rem', fontWeight: 800, margin: 0, color: '#ffffff', letterSpacing: '-0.02em' }}>
              Notificações & Alertas em Tempo Real
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: 'rgba(255,255,255,0.72)' }}>
              Registros instantâneos de entrada/saída RFID, confirmações de chamada e comunicados
            </p>
          </div>

          {/* Hero KPIs */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', position: 'relative', zIndex: 2 }}>
            <div style={{
              background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14,
              padding: '8px 16px', textAlign: 'center', minWidth: 85,
            }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#38bdf8', lineHeight: 1.1 }}>{notificacoes.length}</div>
              <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', marginTop: 3 }}>Total</div>
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14,
              padding: '8px 16px', textAlign: 'center', minWidth: 85,
            }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: naoLidas > 0 ? '#f87171' : '#4ade80', lineHeight: 1.1 }}>{naoLidas}</div>
              <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', marginTop: 3 }}>Não Lidas</div>
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14,
              padding: '8px 16px', textAlign: 'center', minWidth: 85,
            }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#4ade80', lineHeight: 1.1 }}>Ativo</div>
              <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', marginTop: 3 }}>Push Status</div>
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.85fr) minmax(0, 1.15fr)', gap: 20, alignItems: 'start' }}>
          {/* Main Column */}
          <div>
            {loading ? (
              <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>Carregando notificações...</div>
            ) : notificacoes.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 48, background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0' }}>
                <Bell size={40} style={{ color: '#cbd5e1', margin: '0 auto 12px' }} />
                <h3 style={{ margin: '0 0 6px', color: '#0f172a', fontSize: '1rem', fontWeight: 700 }}>Nenhum alerta recente</h3>
                <p style={{ color: '#64748b', fontSize: '0.82rem', margin: 0 }}>Você receberá avisos quando houver movimentações na catraca ou chamadas.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {notificacoes.map((n) => {
                  const tipo = TIPO_ICONE[n.tipoNotificacao] || { icon: Bell, color: '#64748b', bg: '#f1f5f9' };
                  const Icon = tipo.icon;
                  const isLida = !!n.lidaEm;
                  
                  const dataLabel = (() => {
                    try {
                      const d = new Date(n.dataHora);
                      const hoje = new Date();
                      const ontem = new Date(hoje); ontem.setDate(ontem.getDate() - 1);
                      if (d.toDateString() === hoje.toDateString()) return 'Hoje, ' + d.toLocaleTimeString('pt-BR', {hour:'2-digit', minute:'2-digit', timeZone: 'America/Sao_Paulo'});
                      if (d.toDateString() === ontem.toDateString()) return 'Ontem, ' + d.toLocaleTimeString('pt-BR', {hour:'2-digit', minute:'2-digit', timeZone: 'America/Sao_Paulo'});
                      return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', timeZone: 'America/Sao_Paulo' }) + ' às ' + d.toLocaleTimeString('pt-BR', {hour:'2-digit', minute:'2-digit', timeZone: 'America/Sao_Paulo'});
                    } catch { return ''; }
                  })();

                  return (
                    <div
                      key={n.id}
                      onClick={() => abrirDetalhe(n)}
                      style={{
                        background: isLida ? '#fff' : '#f0f9ff',
                        borderRadius: 14, padding: 18, border: '1px solid',
                        borderColor: isLida ? '#e2e8f0' : '#bae6fd',
                        boxShadow: isLida ? '0 1px 4px rgba(0,0,0,0.02)' : '0 4px 12px rgba(37,99,235,0.06)',
                        cursor: 'pointer', transition: 'all 0.15s', display: 'flex', gap: 14, alignItems: 'flex-start',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#2563eb'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = isLida ? '#e2e8f0' : '#bae6fd'; }}
                    >
                      <div style={{
                        width: 42, height: 42, borderRadius: 12, flexShrink: 0,
                        background: tipo.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Icon size={20} style={{ color: tipo.color }} />
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <h4 style={{ margin: 0, fontSize: '0.92rem', fontWeight: isLida ? 600 : 800, color: '#0f172a' }}>
                              {n.titulo || n.tipoNotificacao.replace(/_/g, ' ')}
                            </h4>
                            {!isLida && (
                              <span style={{ fontSize: '0.6rem', fontWeight: 700, color: '#2563eb', background: '#dbeafe', padding: '1px 6px', borderRadius: 4 }}>
                                NÃO LIDO
                              </span>
                            )}
                          </div>
                          <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                            {dataLabel}
                          </span>
                        </div>

                        <p style={{ margin: 0, fontSize: '0.8rem', color: '#475569', lineHeight: 1.4 }}>
                          {n.mensagem}
                        </p>

                        {n.aluno && (
                          <div style={{ marginTop: 8 }}>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.72rem', fontWeight: 700,
                              color: '#1e3a8a', background: '#eff6ff', padding: '3px 10px', borderRadius: 20,
                            }}>
                              <UserAvatar src={n.aluno.fotoUrl} name={n.aluno.nome} role="ALUNO" size={20} />
                              Aluno: {n.aluno.nome}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sidebar Column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <PushStatusCard />
            <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a', margin: '0 0 10px' }}>
                Sobre os Alertas
              </h3>
              <p style={{ fontSize: '0.78rem', color: '#64748b', lineHeight: 1.4, margin: 0 }}>
                Os alertas de presença são sincronizados automaticamente a cada 15 segundos ou instantaneamente via push quando o aluno passa na catraca RFID ou tem chamada confirmada em sala.
              </p>
            </div>
          </div>
        </div>

        <NotificacaoDetalheSheet
          notificacao={selected}
          onClose={() => setSelected(null)}
        />
      </div>
    );
  }

  return (
    <div className="resp-mobile-root">
      {/* ═══ NAVY HEADER ═══ */}
      <div className="resp-navy-header" style={{ padding: 'max(env(safe-area-inset-top, 40px), 40px) 20px 30px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, position: 'relative', zIndex: 10 }}>
          <button 
            onClick={() => navigate('/responsavel/dashboard')} 
            style={{ background: 'transparent', border: 'none', color: 'white', padding: '10px', marginLeft: '-10px', display: 'flex', cursor: 'pointer' }}
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: 'white', letterSpacing: '-0.3px' }}>
              Alertas Recentes
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)', fontWeight: 400 }}>
              {naoLidas > 0 ? `Você tem ${naoLidas} novo(s) alerta(s)` : 'Tudo em dia por aqui'}
            </p>
          </div>
        </div>
      </div>

      {/* ═══ CONTENT AREA ═══ */}
      <div className="resp-content-area" style={{ paddingBottom: 100 }}>
        <PushStatusCard />

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: '0.85rem' }}>Buscando alertas...</div>
        ) : notificacoes.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, background: 'white', borderRadius: 16, boxShadow: '0 1px 8px rgba(0,0,0,0.04)' }}>
            <Bell size={32} style={{ color: '#cbd5e1', margin: '0 auto 12px' }} />
            <p style={{ color: '#64748b', fontSize: '0.85rem', margin: 0, fontWeight: 500 }}>Nenhum alerta recente.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {notificacoes.map((n) => {
              const tipo = TIPO_ICONE[n.tipoNotificacao] || { icon: Bell, color: '#64748b', bg: '#f1f5f9' };
              const Icon = tipo.icon;
              const isLida = !!n.lidaEm;
              
              const dataLabel = (() => {
                try {
                  const d = new Date(n.dataHora);
                  const hoje = new Date();
                  const ontem = new Date(hoje); ontem.setDate(ontem.getDate() - 1);
                  
                  if (d.toDateString() === hoje.toDateString()) return 'Hoje, ' + d.toLocaleTimeString('pt-BR', {hour:'2-digit', minute:'2-digit', timeZone: 'America/Sao_Paulo'});
                  if (d.toDateString() === ontem.toDateString()) return 'Ontem, ' + d.toLocaleTimeString('pt-BR', {hour:'2-digit', minute:'2-digit', timeZone: 'America/Sao_Paulo'});
                  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', timeZone: 'America/Sao_Paulo' }) + ' às ' + d.toLocaleTimeString('pt-BR', {hour:'2-digit', minute:'2-digit', timeZone: 'America/Sao_Paulo'});
                } catch { return ''; }
              })();

              return (
                <div
                  key={n.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => abrirDetalhe(n)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') abrirDetalhe(n); }}
                  style={{
                    background: 'white', borderRadius: 16, padding: '16px',
                    boxShadow: isLida ? '0 1px 4px rgba(0,0,0,0.02)' : '0 4px 12px rgba(37,99,235,0.08)',
                    border: `1px solid ${isLida ? '#f1f5f9' : '#bfdbfe'}`,
                    position: 'relative', overflow: 'hidden', cursor: 'pointer',
                    transition: 'all 0.2s', opacity: isLida ? 0.7 : 1
                  }}
                >
                  {/* Ponto azul de não lida */}
                  {!isLida && (
                    <div style={{ position: 'absolute', top: 16, right: 16, width: 8, height: 8, borderRadius: 4, background: '#3b82f6' }}></div>
                  )}
                  
                  <div style={{ display: 'flex', gap: 14 }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: 12, flexShrink: 0,
                      background: tipo.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Icon size={20} style={{ color: tipo.color }} />
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 2 }}>
                        <h4 style={{ margin: 0, fontSize: '0.9rem', fontWeight: isLida ? 600 : 700, color: '#0f172a', paddingRight: 16 }}>
                          {n.titulo || n.tipoNotificacao.replace(/_/g, ' ')}
                        </h4>
                      </div>
                      <span style={{ display: 'block', fontSize: '0.7rem', color: '#94a3b8', fontWeight: 500, marginBottom: 6 }}>
                        {dataLabel}
                      </span>
                      <p style={{
                        margin: '0', fontSize: '0.8rem', color: '#475569', lineHeight: 1.4,
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                      }}>
                        {n.mensagem}
                      </p>
                      <span style={{ display: 'inline-block', marginTop: 8, fontSize: '0.68rem', fontWeight: 700, color: '#2563eb' }}>
                        Toque para abrir
                      </span>
                      
                      {n.aluno && (
                        <div style={{ marginTop: 10 }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.68rem', fontWeight: 700,
                            color: '#1e3a8a', background: '#eff6ff', padding: '3px 8px 3px 4px', borderRadius: 20,
                          }}>
                            <UserAvatar src={n.aluno.fotoUrl} name={n.aluno.nome} role="ALUNO" size={18} />
                            {n.aluno.nome}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <NotificacaoDetalheSheet
        notificacao={selected}
        onClose={() => setSelected(null)}
      />

      <ResponsavelTabBar activeTab="avisos" />
    </div>
  );
}
